import { isEqual } from "lodash-es";
import { createSelector } from "reselect";

import { EntityType } from "@mapmycustomers/shared/enum";
import { Activity, Company } from "@mapmycustomers/shared/types/entity";
import Route, { RoutePreviewStop } from "@mapmycustomers/shared/types/entity/Route";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import User from "@mapmycustomers/shared/types/User";
import { isDefined, isNotEmpty, isNotEmptyString } from "@mapmycustomers/shared/util/assert";

import { RootState } from "@app/store/rootReducer";
import { parseApiDate, parseApiDateAsNumber } from "@app/util/parsers";
import isRouteComplete from "@app/util/route/isRouteComplete";

import { EditorRouteCompanyStop, EditorRoutePersonStop, EditorRouteStop } from "../types/RouteStop";
import correlateManualStopsToPreview from "../utils/correlateManualStopsToPreview";
import routeStopToPreviewStop, { PREVIEW_METRIC_DEFAULTS } from "../utils/routeStopToPreviewStop";

import { RouteEditorState } from "./index";
import { getEdgeAddressString } from "./utils/buildEdgeLocationPayload";
import resolveActivityStopEntity from "./utils/resolveActivityStopEntity";
import toRouteEditorSnapshot from "./utils/toRouteEditorSnapshot";

type PreviewFields = {
  arrivalTime?: string;
  scheduledTime?: null | string;
  scheduledTimeSkipped?: boolean;
  travelDistance?: number;
  travelTime?: number;
};

export const getRouteEditor = (state: RootState): RouteEditorState =>
  state.scene.route.routeDetails.editor;

export const getRouteEditorName = (state: RootState) => getRouteEditor(state).name;
export const getRouteEditorStartAt = (state: RootState) => getRouteEditor(state).startAt;
export const getRouteEditorOwner = (state: RootState) => getRouteEditor(state).owner;
export const getRouteEditorSharedUserIds = (state: RootState): User["id"][] =>
  getRouteEditor(state).sharedUserIds;
export const getRouteEditorTravelMode = (state: RootState) => getRouteEditor(state).travelMode;
export const getRouteEditorRouteType = (state: RootState) => getRouteEditor(state).routeType;
export const getRouteEditorStartAddress = (state: RootState) => getRouteEditor(state).startAddress;
export const getRouteEditorStartGeoPoint = (state: RootState) =>
  getRouteEditor(state).startGeoPoint;
export const getRouteEditorStartEntity = (state: RootState) => getRouteEditor(state).startEntity;
export const getRouteEditorStartEntityId = (state: RootState) =>
  getRouteEditor(state).startEntityId;
export const getRouteEditorStartEntityType = (state: RootState) =>
  getRouteEditor(state).startEntityType;
export const getRouteEditorStartEdgeGone = (state: RootState): boolean => {
  const editor = getRouteEditor(state);
  return editor.startEntityId != null && !editor.startEntity;
};
export const getRouteEditorEndAddress = (state: RootState) => getRouteEditor(state).endAddress;
export const getRouteEditorEndEntity = (state: RootState) => getRouteEditor(state).endEntity;
export const getRouteEditorEndEntityId = (state: RootState) => getRouteEditor(state).endEntityId;
export const getRouteEditorEndEntityType = (state: RootState) =>
  getRouteEditor(state).endEntityType;
export const getRouteEditorEndEdgeGone = (state: RootState): boolean => {
  const editor = getRouteEditor(state);
  return editor.endEntityId != null && !editor.endEntity;
};
export const getRouteEditorHasEndAddress = (state: RootState): boolean =>
  getRouteEditor(state).endEntityType != null ||
  isNotEmptyString(getEdgeAddressString(getRouteEditor(state).endAddress));
export const getRouteEditorEndGeoPoint = (state: RootState) => getRouteEditor(state).endGeoPoint;

export const getRouteEditorScheduledActivities = (state: RootState): ReadonlyArray<Activity> =>
  getRouteEditor(state).scheduledActivities;

export const getRouteEditorScheduledLoading = (state: RootState): boolean =>
  getRouteEditor(state).scheduledLoading;

export const getRouteEditorStops = (state: RootState): ReadonlyArray<EditorRouteStop> =>
  getRouteEditor(state).stops;

// Everything that counts against MAX_ROUTE_STOPS: editor stops plus scheduled
// activities (Create only — always empty in Edit). Works for both modes.
export const getRouteStopsTotalCount = createSelector(
  [getRouteEditorStops, getRouteEditorScheduledActivities],
  (stops, scheduledActivities) => stops.length + scheduledActivities.length
);

export const getRouteEditorUsedCompanyIds = createSelector(
  getRouteEditorStops,
  (stops): ReadonlyArray<EditorRouteCompanyStop["accountId"]> =>
    stops
      .filter((stop): stop is EditorRouteCompanyStop => stop.entity === EntityType.COMPANY)
      .map((stop) => stop.accountId)
);

export const getRouteEditorUsedPeopleIds = createSelector(
  getRouteEditorStops,
  (stops): ReadonlyArray<EditorRoutePersonStop["contactId"]> =>
    stops
      .filter((stop): stop is EditorRoutePersonStop => stop.entity === EntityType.PERSON)
      .map((stop) => stop.contactId)
);

export const getRouteEditorStopProgress = createSelector(getRouteEditorStops, (stops) => {
  const total = stops.length;
  const completed = stops.filter((stop) => stop.checkedOff).length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { completed, percent, total };
});

export const getEditorRoutePreview = (state: RootState) => getRouteEditor(state).routePreview;
export const getRouteEditorIsPreviewStale = (state: RootState): boolean =>
  getRouteEditor(state).isPreviewStale;

export const getRouteEditorEndArrivalTime = createSelector(
  getEditorRoutePreview,
  getRouteEditorIsPreviewStale,
  (preview, stale) =>
    !preview || stale || !preview.endLocation?.arrivalTime
      ? undefined
      : parseApiDate(preview.endLocation.arrivalTime)
);

export const getRouteEditorBuilding = (state: RootState): boolean => getRouteEditor(state).building;
export const getRouteEditorSaving = (state: RootState): boolean => getRouteEditor(state).saving;

export const getRouteEditorDuplicateStopCount = createSelector(
  getRouteEditorScheduledActivities,
  getRouteEditorStops,
  (activities, stops) => {
    const refKeys = new Set(
      activities
        .map(resolveActivityStopEntity)
        .filter(isDefined)
        .map((ref) => `${ref.entityType}:${ref.id}`)
    );
    return stops.filter((stop) => {
      if (stop.entity === EntityType.COMPANY) {
        return refKeys.has(`${EntityType.COMPANY}:${stop.accountId}`);
      }
      if (stop.entity === EntityType.PERSON) {
        return refKeys.has(`${EntityType.PERSON}:${stop.contactId}`);
      }
      return false;
    }).length;
  }
);

// Editor stops + scheduled activities as RoutePreviewStop[] (activities by startAt, then
// stops by insertion; geoPoint-less entries skipped) — feeds pre-Build route-stop hexagons.
export const getRouteEditorPreviewStops = createSelector(
  getRouteEditorScheduledActivities,
  getRouteEditorStops,
  (activities, stops): RoutePreviewStop[] => {
    const result: RoutePreviewStop[] = [];
    let displayOrder = 0;

    activities
      .slice()
      .sort(
        (left, right) =>
          parseApiDateAsNumber(left.startAt ?? 0) - parseApiDateAsNumber(right.startAt ?? 0)
      )
      .forEach((activity) => {
        const ref = resolveActivityStopEntity(activity);
        const located = activity.account ?? activity.contact;
        const geoPoint = located?.geoPoint;
        if (!ref || !geoPoint) {
          return;
        }
        result.push({
          ...PREVIEW_METRIC_DEFAULTS,
          displayOrder: displayOrder++,
          entity: ref.entityType,
          ...(ref.entityType === EntityType.COMPANY
            ? { accountId: ref.id }
            : { contactId: ref.id }),
          address: located.address,
          geoPoint,
          name: activity.name,
        } as RoutePreviewStop);
      });

    stops.forEach((stop) => {
      const previewStop = routeStopToPreviewStop(stop, displayOrder);
      if (previewStop) {
        result.push(previewStop);
        displayOrder++;
      }
    });

    return result;
  }
);

export const getRouteEditorStopEntityIds = createSelector(
  getRouteEditorScheduledActivities,
  getRouteEditorUsedCompanyIds,
  getRouteEditorUsedPeopleIds,
  (activities, companyIds, personIds) => {
    const companies = new Set<number>(companyIds);
    const people = new Set<number>(personIds);
    activities.forEach((activity) => {
      const activityStopEntity = resolveActivityStopEntity(activity);
      if (activityStopEntity?.entityType === EntityType.COMPANY) {
        companies.add(activityStopEntity.id);
      } else if (activityStopEntity?.entityType === EntityType.PERSON) {
        people.add(activityStopEntity.id);
      }
    });
    return { companyIds: [...companies], personIds: [...people] };
  }
);

export interface OrderedStop {
  geoPoint: GeoPoint;
  id: Activity["id"];
  name: string;
}

type Source =
  | { activity: Activity; kind: "activity" }
  | { kind: "additional"; stop: EditorRouteStop };

// Preview position (0-based index into routePreview.stopTime) keyed by stop identity:
// `${COMPANY}:${accountId}` / `${PERSON}:${contactId}` / `manual:${stopId}`.
// Intentionally NOT gated by isPreviewStale — getRouteEditorOrderedStopsForCreate orders the
// approximate route from this even when the preview is stale. Read-side numbering (the Create
// sidebar) applies the staleness gate itself.
export const getRouteEditorPreviewPositionByKey = createSelector(
  getRouteEditorStops,
  getEditorRoutePreview,
  (stops, routePreview): Map<string, number> => {
    const positionByKey = new Map<string, number>();
    routePreview?.stopTime?.forEach((previewStop, index) => {
      if (previewStop.entity === EntityType.COMPANY) {
        positionByKey.set(`${EntityType.COMPANY}:${previewStop.accountId}`, index);
      } else if (previewStop.entity === EntityType.PERSON) {
        positionByKey.set(`${EntityType.PERSON}:${previewStop.contactId}`, index);
      }
    });
    // The build request excludes checked-off stops, so they must not be walked here either
    // (same positional-pairing constraint as the View selector).
    const manualMatches = correlateManualStopsToPreview(
      stops.filter((stop) => !stop.checkedOff),
      routePreview?.stopTime
    );
    stops.forEach((stop) => {
      if (stop.entity === null && isDefined(stop.id)) {
        const match = manualMatches.get(stop.id);
        if (match) {
          positionByKey.set(`manual:${stop.id}`, match.previewIndex);
        }
      }
    });
    return positionByKey;
  }
);

export const getRouteEditorOrderedStopsForCreate = createSelector(
  getRouteEditorScheduledActivities,
  getRouteEditorStops,
  getRouteEditorPreviewPositionByKey,
  (activities, stops, positionByKey): OrderedStop[] => {
    const naturalOrder: Source[] = [
      ...activities
        .slice()
        .sort(
          (left, right) =>
            parseApiDate(left.startAt ?? 0).getTime() - parseApiDate(right.startAt ?? 0).getTime()
        )
        .map<Source>((activity) => ({ activity, kind: "activity" })),
      ...stops.map<Source>((stop) => ({ kind: "additional", stop })),
    ];

    const candidates = naturalOrder
      .map((source, naturalIndex) => {
        let stop: null | OrderedStop = null;
        let key: string | undefined;
        if (source.kind === "activity") {
          if (source.activity.account?.geoPoint) {
            stop = {
              id: source.activity.id,
              geoPoint: source.activity.account.geoPoint,
              name: source.activity.name,
            };
            key = `${EntityType.COMPANY}:${source.activity.account.id}`;
          } else if (source.activity.contact?.geoPoint) {
            stop = {
              id: source.activity.id,
              geoPoint: source.activity.contact.geoPoint,
              name: source.activity.name,
            };
            key = `${EntityType.PERSON}:${source.activity.contact.id}`;
          }
        } else if (source.stop.entity === EntityType.COMPANY) {
          const geoPoint = source.stop.account?.geoPoint;
          if (geoPoint) {
            stop = { id: source.stop.accountId, geoPoint, name: source.stop.account?.name ?? "" };
            key = `${EntityType.COMPANY}:${source.stop.accountId}`;
          }
        } else if (source.stop.entity === EntityType.PERSON) {
          const geoPoint = source.stop.contact?.geoPoint;
          if (geoPoint) {
            stop = { id: source.stop.contactId, geoPoint, name: source.stop.contact?.name ?? "" };
            key = `${EntityType.PERSON}:${source.stop.contactId}`;
          }
        } else if (source.stop.entity === null) {
          const geoPoint = source.stop.manualGeoPoint;
          if (geoPoint && isDefined(source.stop.id)) {
            stop = { id: source.stop.id, geoPoint, name: source.stop.manualName };
            key = `manual:${source.stop.id}`;
          }
        }
        return stop && key ? { key, naturalIndex, stop } : null;
      })
      .filter(isNotEmpty);

    const INFINITY = Number.POSITIVE_INFINITY;
    candidates.sort((left, right) => {
      const leftPos = positionByKey.has(left.key) ? positionByKey.get(left.key)! : INFINITY;
      const rightPos = positionByKey.has(right.key) ? positionByKey.get(right.key)! : INFINITY;
      if (leftPos !== rightPos) {
        return leftPos - rightPos;
      }
      return left.naturalIndex - right.naturalIndex;
    });

    return candidates.map(({ stop }) => stop);
  }
);

// Sort ascending by preview position; unmatched (undefined) sink to the tail. No explicit
// tiebreak — Array.prototype.sort is stable, so equal/undefined positions keep input order.
const byPreviewIndex = (
  left: { previewIndex: number | undefined },
  right: { previewIndex: number | undefined }
): number =>
  (left.previewIndex ?? Number.POSITIVE_INFINITY) -
  (right.previewIndex ?? Number.POSITIVE_INFINITY);

export interface OrderedScheduledActivity {
  activity: Activity;
  previewIndex: number | undefined;
}

// One entry per activity, NEVER filtered: unmatched activities stay with previewIndex=undefined
// and sort to the tail, so the section's header count (activities.length) matches the rows.
// Numbering is gated on a fresh preview, matching the map (Create/index.tsx numbered-pin gate).
export const getRouteEditorOrderedScheduledActivities = createSelector(
  getRouteEditorScheduledActivities,
  getRouteEditorPreviewPositionByKey,
  getRouteEditorIsPreviewStale,
  (activities, positionByKey, isPreviewStale): OrderedScheduledActivity[] => {
    const active = !isPreviewStale && positionByKey.size > 0;
    const ordered = activities.map((activity): OrderedScheduledActivity => {
      if (!active) {
        return { activity, previewIndex: undefined };
      }
      const ref = resolveActivityStopEntity(activity);
      const key = ref ? `${ref.entityType}:${ref.id}` : undefined;
      return {
        activity,
        previewIndex: key && positionByKey.has(key) ? positionByKey.get(key) : undefined,
      };
    });
    return active ? ordered.sort(byPreviewIndex) : ordered;
  }
);

// Preview-map key for an editor stop; undefined for stops that can't appear in the preview.
const editorStopPreviewKey = (stop: EditorRouteStop): string | undefined => {
  if (stop.entity === EntityType.COMPANY) {
    return `${EntityType.COMPANY}:${stop.accountId}`;
  }
  if (stop.entity === EntityType.PERSON) {
    return `${EntityType.PERSON}:${stop.contactId}`;
  }
  if (stop.entity === null && isDefined(stop.id)) {
    return `manual:${stop.id}`;
  }
  return undefined;
};

export interface OrderedAdditionalStop {
  previewIndex: number | undefined;
  stop: EditorRouteStop;
}

// One entry per stop, NEVER filtered (see getRouteEditorOrderedScheduledActivities): unmatched
// stops keep previewIndex=undefined and sink to the tail, so the header count (stops.length)
// matches the rendered rows. Numbering is gated on a fresh preview, matching the map.
export const getRouteEditorOrderedAdditionalStops = createSelector(
  getRouteEditorStops,
  getRouteEditorPreviewPositionByKey,
  getRouteEditorIsPreviewStale,
  (stops, positionByKey, isPreviewStale): OrderedAdditionalStop[] => {
    const active = !isPreviewStale && positionByKey.size > 0;
    const ordered = stops.map((stop): OrderedAdditionalStop => {
      if (!active) {
        return { previewIndex: undefined, stop };
      }
      const key = editorStopPreviewKey(stop);
      return {
        previewIndex: key && positionByKey.has(key) ? positionByKey.get(key) : undefined,
        stop,
      };
    });
    return active ? ordered.sort(byPreviewIndex) : ordered;
  }
);

export const getRouteEditorDisplayStops = createSelector(
  [getRouteEditorStops, getEditorRoutePreview, getRouteEditorIsPreviewStale],
  (stops, routePreview, isPreviewStale): ReadonlyArray<EditorRouteStop> => {
    // A completed route shows the same thing in both modes: the approximate line, and stops with
    // no leg times. The preview stays in editor state because Save is gated on it.
    if (isPreviewStale || !routePreview?.stopTime?.length || isRouteComplete(stops)) {
      return stops;
    }
    const previewByCompanyId = new Map<Company["id"], PreviewFields>();
    const previewByPersonId = new Map<number, PreviewFields>();
    routePreview.stopTime.forEach((previewStop) => {
      const previewFields = {
        arrivalTime: previewStop.arrivalTime,
        scheduledTime: previewStop.scheduledTime,
        scheduledTimeSkipped: previewStop.scheduledTimeSkipped,
        travelDistance: previewStop.travelDistance,
        travelTime: previewStop.travelTime,
      };
      if (previewStop.entity === EntityType.COMPANY) {
        previewByCompanyId.set(previewStop.accountId, previewFields);
      } else if (previewStop.entity === EntityType.PERSON) {
        previewByPersonId.set(previewStop.contactId, previewFields);
      }
    });
    // The build request excludes checked-off stops, so they must not be walked here either
    // (same positional-pairing constraint as the View selector).
    const manualMatches = correlateManualStopsToPreview(
      stops.filter((stop) => !stop.checkedOff),
      routePreview.stopTime
    );
    return stops.map((stop) => {
      const preview =
        stop.entity === EntityType.COMPANY
          ? previewByCompanyId.get(stop.accountId)
          : stop.entity === EntityType.PERSON
            ? previewByPersonId.get(stop.contactId)
            : stop.entity === null && isDefined(stop.id)
              ? manualMatches.get(stop.id)
              : undefined;
      if (!preview) {
        return stop;
      }
      return {
        ...stop,
        arrivalTime: preview.arrivalTime,
        scheduledTime: stop.scheduledTime ?? preview.scheduledTime ?? null,
        scheduledTimeSkipped: preview.scheduledTimeSkipped,
        travelDistance: preview.travelDistance,
        travelTime: preview.travelTime,
      };
    });
  }
);

export const getRouteEditorHasPinnedStops = createSelector(
  [getRouteEditorDisplayStops],
  (stops): boolean => stops.some((stop) => isNotEmpty(stop.scheduledTime))
);

export const getOriginalRoute = (state: RootState): Route | undefined =>
  getRouteEditor(state).originalRoute;
export const getRouteEditorAllottedTime = (state: RootState): number =>
  getRouteEditor(state).allottedTime;
export const isRouteEditorInitializing = (state: RootState): boolean =>
  getRouteEditor(state).initializing;

export const doesRouteEditorHaveChanges = createSelector(getRouteEditor, (editor): boolean => {
  if (!editor.initialSnapshot) {
    // Create mode: any user-entered content. Scheduled activities are auto-fetched,
    // so they only count as a change once the user has removed some (length < total).
    return (
      editor.stops.length > 0 ||
      editor.startEntityId != null ||
      editor.startEntityType != null ||
      editor.startAddress != null ||
      editor.startGeoPoint != null ||
      editor.endEntityId != null ||
      editor.endEntityType != null ||
      editor.endAddress != null ||
      editor.endGeoPoint != null ||
      editor.scheduledActivities.length < editor.scheduledActivitiesTotal ||
      editor.sharedUserIds.length > 1
    );
  }
  // Edit mode: compare against the snapshot frozen at init — see the
  // initialSnapshot field comment in ./index.ts for why it is not re-derived here.
  return !isEqual(toRouteEditorSnapshot(editor), editor.initialSnapshot);
});

// Arms the leave-confirmation <Prompt>. Suppressed while a save/create is in flight:
// saving flips true at saveRoute/createRoute.request (well before the success+redirect),
// giving <Prompt> time to drop its history.block during the network round-trip so the
// post-save push isn't intercepted by a stale block.
export const getRouteEditorShouldShowLeavePrompt = createSelector(
  doesRouteEditorHaveChanges,
  getRouteEditorSaving,
  (hasChanges, saving): boolean => hasChanges && !saving
);
